import type { ReactNode } from 'react';

/**
 * The small stroked icons the sign-in screens use: the leading marks inside
 * the inputs, the shield on the challenge card and the three footer links.
 *
 * They live here rather than in `components/` because they are only ever
 * drawn on these five screens, and they take their colour from
 * `currentColor` so the same drawing works on the navy panel and on the
 * themed card.
 */
function icon(size: number, children: ReactNode): ReactNode {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable="false"
    >
      {children}
    </svg>
  );
}

export const MailIcon = ({ size = 16 }: { size?: number }) =>
  icon(size, <><rect x="1.75" y="3.25" width="12.5" height="9.5" rx="1.5" /><path d="M2.5 4.5L8 8.75 13.5 4.5" /></>);

export const LockIcon = ({ size = 16 }: { size?: number }) =>
  icon(size, <><rect x="3.5" y="7" width="9" height="6.5" rx="1.5" /><path d="M5.5 7V5a2.5 2.5 0 015 0v2" /></>);

export const ShieldIcon = ({ size = 16 }: { size?: number }) =>
  icon(size, <><path d="M8 1.5l5.5 2.4v3.6c0 4-5.5 7-5.5 7s-5.5-3-5.5-7V3.9L8 1.5z" /><rect x="6" y="7" width="4" height="3.4" rx="0.8" /><path d="M6.9 7V6.1a1.1 1.1 0 012.2 0V7" /></>);

export const BookIcon = ({ size = 16 }: { size?: number }) =>
  icon(size, <><path d="M2 3h4a2 2 0 012 2v8a1.6 1.6 0 00-1.6-1.2H2z" /><path d="M14 3h-4a2 2 0 00-2 2v8a1.6 1.6 0 011.6-1.2H14z" /></>);

export const StatusIcon = ({ size = 16 }: { size?: number }) =>
  icon(size, <><path d="M8 1.5l5.5 2.4v3.6c0 4-5.5 7-5.5 7s-5.5-3-5.5-7V3.9L8 1.5z" /><path d="M5.5 8l2 2 3.5-3.5" /></>);

export const GlobeIcon = ({ size = 16 }: { size?: number }) =>
  icon(size, <><circle cx="8" cy="8" r="6.2" /><path d="M1.8 8h12.4M8 1.8c1.7 1.8 2.6 4 2.6 6.2S9.7 12.4 8 14.2C6.3 12.4 5.4 10.2 5.4 8S6.3 3.6 8 1.8z" /></>);

export const ContrastIcon = ({ size = 16 }: { size?: number }) =>
  icon(size, <><circle cx="8" cy="8" r="6.2" /><path d="M8 1.8a6.2 6.2 0 000 12.4z" fill="currentColor" stroke="none" /></>);

export const ArrowLeftIcon = ({ size = 16 }: { size?: number }) =>
  icon(size, <><path d="M13 8H3.5M7 4L3.2 8 7 12" /></>);

export const CheckIcon = ({ size = 16 }: { size?: number }) =>
  icon(size, <><circle cx="8" cy="8" r="6.2" /><path d="M5.3 8.2l1.9 1.9 3.6-3.9" /></>);

export const DotIcon = ({ size = 16 }: { size?: number }) =>
  icon(size, <circle cx="8" cy="8" r="6.2" />);
